import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import PriceChart from '../components/PriceChart';
import TransactionList from '../components/TransactionList';
import { searchSoldCards } from '../api/cards';
import type { Transaction, CardInfo } from '../types';

export default function CardDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // 从路由 state 中获取卡片信息
  const cardInfo = location.state?.card as CardInfo | undefined;
  const imageUrl = cardInfo?.image_url_at_self_platform || cardInfo?.image_url;

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPlatform, setSelectedPlatform] = useState<string | undefined>();

  // 用于防止 StrictMode 下初始加载重复调用
  const initialFetchRef = useRef(false);

  // 获取可用平台列表
  const platforms = Array.from(new Set(transactions.map((t) => t.platform)));

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!id) return;

      setIsLoading(true);
      setError(null);

      try {
        const idLabel = id || cardInfo?.uid;
        if (!idLabel) {
          setError('缺少卡片编号');
          setIsLoading(false);
          return;
        }
        const response = await searchSoldCards(idLabel, currentPage, 20);

        if (response.code === 200) {
          if (currentPage === 1) {
            setTransactions(response.data);
          } else {
            setTransactions((prev) => [...prev, ...response.data]);
          }
          setTotalPages(response.totalPages);
          setTotalCount(response.total);
        } else {
          setError(response.message || '获取数据失败');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || '获取数据失败');
      } finally {
        setIsLoading(false);
      }
    };

    // 防止 StrictMode 下初始加载重复调用，但允许分页加载
    if (currentPage === 1) {
      if (initialFetchRef.current) return;
      initialFetchRef.current = true;
    }

    fetchTransactions();
  }, [id, cardInfo, currentPage]);

  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // 过滤后的交易记录
  const filteredTransactions = selectedPlatform
    ? transactions.filter((t) => t.platform === selectedPlatform)
    : transactions;
  const chartIdLabel = id || cardInfo?.uid;

  return (
    <Layout>
      {/* 返回按钮 */}
      <button
        onClick={handleBack}
        className="flex items-center gap-1 text-secondary hover:text-primary transition-colors mb-4"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        <span className="text-sm">返回</span>
      </button>

      {/* 卡片信息 */}
      {cardInfo && (
        <div className="bg-white border border-border rounded-lg p-4 mb-6">
          <div className="flex gap-4">
            {/* 卡片图片 */}
            <div className="w-24 aspect-[63/88] flex-shrink-0 bg-background-secondary rounded overflow-hidden">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={cardInfo.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-secondary-light">
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              )}
            </div>

            {/* 卡片详情 */}
            <div className="flex-1">
              <h1 className="text-xl font-semibold text-primary">{cardInfo.name}</h1>
              {cardInfo.number && (
                <p className="text-sm text-secondary mt-1">编号: {cardInfo.number}</p>
              )}
              {cardInfo.language && (
                <p className="text-sm text-secondary mt-0.5">语言: {cardInfo.language}</p>
              )}
              {cardInfo.type && (
                <p className="text-sm text-secondary-light mt-0.5">类型: {cardInfo.type}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 平台筛选 */}
      {platforms.length > 1 && (
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-secondary">平台筛选:</span>
          <button
            onClick={() => setSelectedPlatform(undefined)}
            className={`text-sm px-3 py-1 rounded-full transition-colors ${
              !selectedPlatform
                ? 'bg-primary text-white'
                : 'bg-white border border-border text-secondary hover:border-primary'
            }`}
          >
            全部
          </button>
          {platforms.map((platform) => (
            <button
              key={platform}
              onClick={() => setSelectedPlatform(platform)}
              className={`text-sm px-3 py-1 rounded-full transition-colors ${
                selectedPlatform === platform
                  ? 'bg-primary text-white'
                  : 'bg-white border border-border text-secondary hover:border-primary'
              }`}
            >
              {platform}
            </button>
          ))}
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* 首次加载：显示页面级 loading */}
      {isLoading && currentPage === 1 ? (
        <div className="bg-white rounded-lg border border-border p-8">
          <div className="h-[600px] flex flex-col items-center justify-center">
            <div className="relative w-16 h-16 mb-4">
              <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-blue-500 rounded-full border-t-transparent animate-spin"></div>
            </div>
            <p className="text-secondary text-sm">加载数据中...</p>
          </div>
        </div>
      ) : (
        <>
          {/* 价格曲线 */}
          <div className="mb-6">
            <PriceChart 
              idLabel={chartIdLabel}
              selectedPlatform={selectedPlatform}
            />
          </div>

          {/* 成交记录列表 */}
          <TransactionList
            transactions={filteredTransactions}
            totalCount={totalCount}
            loadedCount={transactions.length}
            isLoading={isLoading && currentPage > 1}
            onLoadMore={handleLoadMore}
            hasMore={currentPage < totalPages}
          />
        </>
      )}
    </Layout>
  );
}
